/**
 * @nanokart/tryon-studio-sdk
 * Catalog Provider Interface
 * 
 * Abstracts inventory/catalog fetching.
 * Enables Shopify catalog, static items, or Nanokart inventory interchangeably.
 */

import { CatalogItem, CatalogFilters, CatalogPagination, CatalogResult } from '../../types/config';

// ============================================================================
// Provider Capabilities
// ============================================================================

export interface ProviderCapabilities {
  supportsFiltering: boolean;
  supportsPagination: boolean;
  supportsSearch: boolean;
}

// ============================================================================
// Catalog Provider Interface
// ============================================================================

export interface CatalogProvider {
  /**
   * List items with optional filters and pagination
   */
  list(filters?: CatalogFilters, pagination?: CatalogPagination): Promise<CatalogResult>;

  /**
   * Get single item by ID
   */
  getById(productId: string): Promise<CatalogItem | null>;

  /**
   * Search items
   */
  search(query: string, pagination?: CatalogPagination): Promise<CatalogResult>;

  /**
   * Provider capabilities - DRIVES UI VISIBILITY
   */
  capabilities: ProviderCapabilities;
}

// ============================================================================
// UI Visibility derived from capabilities
// ============================================================================

export interface UIVisibility {
  /** Show search bar */
  showSearch: boolean;
  /** Show filter panel */
  showFilters: boolean;
  /** Show load more / infinite scroll */
  showLoadMore: boolean;
  /** Force single item view */
  forceSingleItemView: boolean;
  /** Show inventory carousel (vs single item) */
  showInventoryCarousel: boolean;
}

/**
 * Derive UI visibility from provider capabilities and config
 */
export function deriveUIVisibility(
  provider: CatalogProvider,
  options: {
    singleItemMode?: boolean;
    items?: CatalogItem[];
    mode?: string;
  }
): UIVisibility {
  const { singleItemMode, items, mode } = options;

  // Provider has capabilities → it can return multiple items → show carousel
  const providerCanReturnMultiple =
    provider && typeof provider.list === 'function';

  // Force single item view only when explicitly requested OR exactly 1 static item
  const forceSingleItemView =
    singleItemMode === true ||
    (items !== undefined && items.length === 1);

  return {
    showSearch: provider.capabilities.supportsSearch && !forceSingleItemView,
    showFilters: provider.capabilities.supportsFiltering && !forceSingleItemView,
    showLoadMore: provider.capabilities.supportsPagination && !forceSingleItemView,
    forceSingleItemView,
    showInventoryCarousel:
      !forceSingleItemView &&
      (providerCanReturnMultiple || (items ? items.length > 1 : false)),
  };
}
